import { type Biome, type WorldMapNode } from '@goodtime/shared';
import { resolveEntryUrl } from '@goodtime/engine';
// Stage 3 install-excision (2026-06-06) — biome env PNGs (~24MB
// across the 5 biomes) + boss icon used to bundle via static imports
// here. Now resolved through {@link resolveEntryUrl} so production
// builds fetch from CloudFront. The three small UI icons stay
// bundled — they're shared chrome under 100KB each.
import friendsIconUrl from '../../assets/icon/friends.png';
import shareIconUrl from '../../assets/icon/share.png';
import leaderboardIconUrl from '../../assets/icon/leaderboard.png';
import { CornerSpark, PearlStud, Sparkle } from '../dialog/DialogOrnaments';

/** biome-id → repo-relative path under apps/mobile/src/assets/.
 *  Resolved via {@link resolveEntryUrl} at call time so the URL
 *  comes from CDN (manifest hit) or the Vite dev fallback. */
const BIOME_ENV_PATH: Record<Biome['id'], string> = {
  kingdom: 'environment/forest.png',
  cove: 'environment/underwater.png',
  frost: 'environment/frost.png',
  frontier: 'environment/frontier.png',
  neon: 'environment/neon.png',
  egypt: 'environment/egypt.png',
  candy: 'environment/sherbet_square.png',
};

const BOSS_ICON_PATH = 'map_node/boss.png';

/**
 * Per-biome palette driving the dialogue chrome — ribbon gradient,
 * border tint, corner-spark fill, CTA gradient, deep interior.
 *
 * <p>Sourced per-biome rather than the shared {@code Biome.accent}
 * alone (which is a single dark hex unsuitable for a hero gradient).
 * Three stops per biome:
 * <ul>
 *   <li><b>primary</b> — vibrant mid-tone for the ribbon banner</li>
 *   <li><b>accent</b> — bright trim/highlight for sparks + the
 *       active CTA</li>
 *   <li><b>deep</b> — near-black biome-tinted base for the radial
 *       gradient interior</li>
 * </ul>
 */
const BIOME_PALETTE: Record<
  Biome['id'],
  { primary: string; accent: string; deep: string; ctaTop: string; ctaBot: string }
> = {
  kingdom: {
    primary: '#3E7A3E',
    accent: '#FFC940',
    deep: '#0E2410',
    ctaTop: '#FFD96B',
    ctaBot: '#FFC940',
  },
  cove: {
    primary: '#3D8BC4',
    accent: '#FFD89B',
    deep: '#0E2236',
    ctaTop: '#FFD89B',
    ctaBot: '#FF9B7A',
  },
  frost: {
    primary: '#4A6FA5',
    accent: '#C5D9F1',
    deep: '#0E1A2E',
    ctaTop: '#E8F1FB',
    ctaBot: '#A6BFE0',
  },
  frontier: {
    primary: '#C25A3F',
    accent: '#F4B860',
    deep: '#2A130A',
    ctaTop: '#F4B860',
    ctaBot: '#C25A3F',
  },
  neon: {
    primary: '#FF2EA8',
    accent: '#FFD700',
    deep: '#1A0B3D',
    ctaTop: '#FF66C4',
    ctaBot: '#FF2EA8',
  },
  egypt: {
    primary: '#C9962E',
    accent: '#F2C23A',
    deep: '#2A1C08',
    ctaTop: '#F6D98A',
    ctaBot: '#C9962E',
  },
  candy: {
    primary: '#E84FBF',
    accent: '#FFD24D',
    deep: '#2A0E33',
    ctaTop: '#FF9FD0',
    ctaBot: '#B04CFF',
  },
};

export interface WheelNodePreviewProps {
  node: WorldMapNode;
  biome: Biome;
  onClose: () => void;
  onEnter: (node: WorldMapNode) => void;
}

interface PlayOption {
  id: 'bots' | 'invite' | 'join' | 'public';
  iconSrc: string;
  label: string;
  caption: string;
  enabled: boolean;
}

const PLAY_OPTIONS: readonly PlayOption[] = [
  {
    id: 'bots',
    iconSrc: resolveEntryUrl({ path: BOSS_ICON_PATH }) ?? '',
    label: 'Play with the cast',
    caption: 'Solo round vs. the eight regulars',
    enabled: true,
  },
  {
    id: 'invite',
    iconSrc: friendsIconUrl,
    label: 'Invite friends',
    caption: 'Open a private table — bots fill empty seats',
    enabled: false,
  },
  {
    id: 'join',
    iconSrc: shareIconUrl,
    label: "Join a friend's table",
    caption: 'Enter the code or tap their invite link',
    enabled: false,
  },
  {
    id: 'public',
    iconSrc: leaderboardIconUrl,
    label: 'Public table',
    caption: 'Matchmake with anyone playing right now',
    enabled: false,
  },
];

/**
 * Game-type selection dialogue for Wheel-of-Fortune nodes on the
 * world map. Tap → this card opens; the player picks a play mode and
 * the dialogue routes them into the room.
 *
 * <p>Chrome follows the story-card style of {@link DialogOrnaments}
 * (gold double-border, ribbon title overhanging the top, scattered
 * sparkles, corner sparks). The whole palette is re-tinted per
 * biome via {@link BIOME_PALETTE} so Greendale reads as warm forest
 * gold, Cove as coral-and-sand, etc.
 *
 * <p>v1: only "Play with the cast" (bots only) is functional. The
 * social options (invite / join / public) ship as disabled rows
 * with a "Soon" badge so the surface is staked out for the social
 * design phase to fill in.
 */
export function WheelNodePreview({
  node,
  biome,
  onClose,
  onEnter,
}: WheelNodePreviewProps) {
  const envUrl = resolveEntryUrl({ path: BIOME_ENV_PATH[biome.id] }) ?? '';
  const palette = BIOME_PALETTE[biome.id];

  const [primaryName, ...rest] = node.name.split(' · ');
  const subName = rest.length > 0 ? rest.join(' · ') : null;

  const handlePick = (option: PlayOption): void => {
    if (!option.enabled) return;
    if (option.id === 'bots') {
      onEnter(node);
      onClose();
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'gtFadeIn 0.2s ease-out',
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 360,
          marginTop: 28, // leaves room for the ribbon to overhang
          animation: 'gtPop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Title ribbon — straddles the top edge of the card. Biome-
            themed gradient with the biome name in white caps. Mirrors
            the story-card violet ribbon, re-tinted per biome. */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'translateY(-50%)',
          }}
        >
          <div
            style={{
              position: 'relative',
              borderRadius: 999,
              border: `3px solid ${palette.accent}E6`,
              background: `linear-gradient(180deg, ${palette.primary} 0%, ${shade(palette.primary, -25)} 100%)`,
              padding: '8px 36px',
              boxShadow:
                '0 4px 10px rgba(0,0,0,0.5), inset 0 -2px 3px rgba(0,0,0,0.35), inset 0 2px 3px rgba(255,255,255,0.4)',
              whiteSpace: 'nowrap',
            }}
          >
            <PearlStud
              side="left"
              background={`radial-gradient(circle at 35% 30%, ${palette.accent}, ${shade(palette.accent, -25)} 60%, ${shade(palette.accent, -55)})`}
            />
            <PearlStud
              side="right"
              background={`radial-gradient(circle at 35% 30%, ${palette.accent}, ${shade(palette.accent, -25)} 60%, ${shade(palette.accent, -55)})`}
            />
            <h2
              style={{
                margin: 0,
                fontSize: 13,
                fontWeight: 900,
                letterSpacing: 1,
                color: '#fff',
                textShadow: '0 2px 0 rgba(0,0,0,0.55)',
                fontFamily: 'inherit',
              }}
            >
              {biome.chapter.toUpperCase()} · {biome.name.toUpperCase()}
            </h2>
          </div>
        </div>

        {/* Card body — gold-bordered, biome-deep radial gradient. */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 24,
            border: `3px solid ${palette.accent}D9`,
            color: '#E9F0FF',
            background: `radial-gradient(ellipse at 50% 0%, ${shade(palette.deep, 22)} 0%, ${palette.deep} 65%, ${shade(palette.deep, -25)} 100%)`,
            boxShadow:
              '0 30px 60px rgba(0,0,0,0.55), inset 0 0 0 2px rgba(255,255,255,0.06)',
          }}
        >
          {/* corner ornaments — biome-accent tinted */}
          <CornerSpark position="top-left" fill={palette.accent} />
          <CornerSpark position="top-right" fill={palette.accent} />
          <CornerSpark position="bottom-left" fill={palette.accent} />
          <CornerSpark position="bottom-right" fill={palette.accent} />

          {/* scattered sparkles */}
          <Sparkle top="28%" left="7%" size={6} rotation={15} fill={palette.accent} />
          <Sparkle top="44%" left="93%" size={7} rotation={-22} fill={palette.accent} />
          <Sparkle top="68%" left="6%" size={5} rotation={40} fill={palette.accent} />
          <Sparkle top="78%" left="86%" size={6} rotation={5} fill={palette.accent} />

          {/* Biome panorama hero strip — full-bleed across the top. */}
          <div
            style={{
              position: 'relative',
              height: 130,
              backgroundImage: `url(${envUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, ${palette.deep}CC 100%)`,
              }}
            />
          </div>

          {/* Body */}
          <div style={{ position: 'relative', zIndex: 1, padding: '14px 20px 18px' }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: palette.accent,
              }}
            >
              {primaryName}
            </div>
            {subName ? (
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  marginTop: 2,
                  color: '#fff',
                }}
              >
                {subName}
              </div>
            ) : null}

            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
                color: '#A8B5D9',
                marginTop: 16,
                marginBottom: 10,
              }}
            >
              How would you like to play?
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PLAY_OPTIONS.map((opt) => (
                <PlayOptionRow
                  key={opt.id}
                  option={opt}
                  palette={palette}
                  onPick={() => handlePick(opt)}
                />
              ))}
            </div>

            <div
              style={{
                fontSize: 10,
                opacity: 0.55,
                marginTop: 14,
                textAlign: 'center',
              }}
            >
              Free to play · Coins are pretend · No real money
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlayOptionRow({
  option,
  palette,
  onPick,
}: {
  option: PlayOption;
  palette: typeof BIOME_PALETTE[Biome['id']];
  onPick: () => void;
}) {
  const enabled = option.enabled;
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={!enabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 14px',
        borderRadius: 14,
        cursor: enabled ? 'pointer' : 'default',
        textAlign: 'left',
        fontFamily: 'inherit',
        transition: 'transform 80ms ease-out, box-shadow 80ms ease-out',
        ...(enabled
          ? {
              background: `linear-gradient(180deg, ${palette.ctaTop} 0%, ${palette.ctaBot} 100%)`,
              color: '#2A1700',
              border: `2px solid ${palette.accent}`,
              boxShadow: `0 4px 0 ${shade(palette.ctaBot, -35)}, 0 8px 16px rgba(0,0,0,0.3)`,
            }
          : {
              background: 'rgba(255,255,255,0.05)',
              color: '#A8B5D9',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: 'none',
              opacity: 0.7,
            }),
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-hidden
      >
        <img
          src={option.iconSrc}
          alt=""
          style={{
            width: 40,
            height: 40,
            objectFit: 'contain',
            filter: enabled ? 'none' : 'grayscale(0.6) brightness(0.85)',
            opacity: enabled ? 1 : 0.7,
            pointerEvents: 'none',
          }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: 0.2,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>{option.label}</span>
          {!enabled ? (
            <span
              style={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: 1,
                textTransform: 'uppercase',
                padding: '2px 6px',
                borderRadius: 6,
                background: `${palette.accent}28`,
                color: palette.accent,
                border: `1px solid ${palette.accent}66`,
              }}
            >
              Soon
            </span>
          ) : null}
        </div>
        <div
          style={{
            fontSize: 11,
            marginTop: 2,
            opacity: enabled ? 0.75 : 0.85,
          }}
        >
          {option.caption}
        </div>
      </div>
    </button>
  );
}

/**
 * Lighten or darken a hex colour by a percentage (-100..100). Pure
 * arithmetic on the RGB triplet — fine for our purposes (gradient
 * stops, shadow strokes) where perceptual accuracy doesn't matter.
 */
function shade(hex: string, percent: number): string {
  const m = hex.replace('#', '');
  if (m.length !== 6) return hex;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  const f = (v: number) => {
    const t = percent < 0 ? 0 : 255;
    const p = Math.abs(percent) / 100;
    return Math.round((t - v) * p + v);
  };
  return (
    '#' +
    [f(r), f(g), f(b)]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
  );
}
